import React, { useEffect, useRef, useState } from 'react';
import { useEnteredView } from '@fiverr-private/hooks';
import { withVideoQuality } from '../../shared/cloudinary';
import { MIXPANEL_EVENTS, sendMixpanelEvent } from '../../shared/tracking';
import useNetworkVideoQuality from '../../hooks/useNetworkVideoQuality';
import { CINEMATIC_CTA_TEXT, CINEMATIC_STEPS, CINEMATIC_SUBTITLE, CINEMATIC_TITLE } from './constants';
import {
  bottomGradient,
  card,
  cardImage,
  cardOverlay,
  cardTitle,
  columnScrollDown,
  columnScrollUp,
  ctaButton,
  ctaSection,
  gridWrapper,
  headerWrap,
  masonryColumn,
  masonryColumnDesktopOnly,
  masonryGrid,
  section,
  subtitleText,
  titleText,
  topGradient,
} from './styles.ve.css';

interface CinematicSectionProps {
  onPrimaryCtaClick: () => void;
}

const COLUMN_1_ITEMS = [0, 3, 6];
const COLUMN_2_ITEMS = [1, 4, 7];
const COLUMN_3_ITEMS = [2, 5, 8];

const renderCards = (indices: number[], isVisible: boolean, quality: string, keyPrefix = '') =>
  indices.map((index) => {
    const step = CINEMATIC_STEPS[index];

    return (
      <div key={`${step.id}${keyPrefix}`} className={card}>
        {step.videoUrl ? (
          <video
            className={cardImage}
            src={isVisible ? withVideoQuality(step.videoUrl, quality) : undefined}
            poster={step.posterUrl}
            autoPlay={isVisible}
            preload="none"
            loop
            muted
            playsInline
          />
        ) : (
          <img className={cardImage} src={isVisible ? step.imageUrl : undefined} alt={step.title} loading="lazy" />
        )}
        <div className={cardOverlay} />
        <p className={cardTitle}>{step.title}</p>
      </div>
    );
  });

const CinematicSection: React.FC<CinematicSectionProps> = ({ onPrimaryCtaClick }) => {
  const trackingRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const quality = useNetworkVideoQuality();

  useEnteredView(trackingRef, {
    threshold: 0.1,
    onEntered: () => {
      sendMixpanelEvent({ eventName: MIXPANEL_EVENTS.CINEMATIC_SECTION_VIEWED });
    },
  });

  useEffect(() => {
    const element = gridRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={trackingRef} className={section}>
      <div className={headerWrap}>
        <h2 className={titleText}>{CINEMATIC_TITLE}</h2>
        <p className={subtitleText}>{CINEMATIC_SUBTITLE}</p>
      </div>

      <div ref={gridRef} className={gridWrapper}>
        <div className={masonryGrid}>
          <div className={`${masonryColumn} ${columnScrollDown}`}>
            {renderCards(COLUMN_1_ITEMS, isVisible, quality)}
            {renderCards(COLUMN_1_ITEMS, isVisible, quality, '-dup')}
          </div>

          <div className={`${masonryColumn} ${columnScrollUp}`}>
            {renderCards(COLUMN_2_ITEMS, isVisible, quality)}
            {renderCards(COLUMN_2_ITEMS, isVisible, quality, '-dup')}
          </div>

          <div className={`${masonryColumnDesktopOnly} ${columnScrollDown}`}>
            {renderCards(COLUMN_3_ITEMS, isVisible, quality)}
            {renderCards(COLUMN_3_ITEMS, isVisible, quality, '-dup')}
          </div>
        </div>

        <div className={topGradient} />
        <div className={bottomGradient} />
      </div>

      <div className={ctaSection}>
        <button
          type="button"
          className={ctaButton}
          onClick={() => {
            sendMixpanelEvent({ eventName: MIXPANEL_EVENTS.CLICKED_CINEMATIC_CTA });
            onPrimaryCtaClick();
          }}
        >
          {CINEMATIC_CTA_TEXT}
        </button>
      </div>
    </div>
  );
};

export default CinematicSection;
